import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

const Journey = ({ steps }) => (
    <section className="mt-28" data-testid="service-journey">
        <div className="eyebrow eyebrow-gold mb-4">Your Glow Journey</div>
        <h2 className="serif text-4xl md:text-5xl leading-[1.05] max-w-2xl">Three treatments. One coordinated visit.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-t border-[#A89070]/30">
            {steps.map((s, i) => (
                <div
                    key={s.step}
                    data-testid={`journey-step-${s.step}`}
                    className={`py-10 md:px-8 ${i === 0 ? "md:pl-0" : "md:border-l border-[#A89070]/30"} border-b md:border-b-0 border-[#A89070]/30`}
                >
                    <div className="flex items-baseline gap-4">
                        <span className="serif italic text-5xl text-[#A89070] leading-none">{s.step}</span>
                        <span className="text-[11px] tracking-[0.3em] uppercase text-[#262422]">{s.name}</span>
                    </div>
                    <h3 className="serif text-2xl mt-6">{s.title}</h3>
                    <p className="mt-4 text-[#544638] leading-relaxed text-[15px]">{s.text}</p>
                </div>
            ))}
        </div>
    </section>
);

const Why = ({ intro, items }) => (
    <section className="mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="service-why">
        <div className="lg:col-span-4">
            <div className="eyebrow eyebrow-gold mb-4">Why It's Special</div>
            <h2 className="serif text-4xl leading-[1.08]">More than a facial.</h2>
            <p className="mt-6 text-[#544638] leading-relaxed">{intro}</p>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#A89070]/25 border border-[#A89070]/25">
            {items.map((w) => (
                <div key={w.title} className="bg-[#F2EDE2] p-8 hover:bg-[#EDE6D6] transition-colors duration-300">
                    <Sparkles size={16} className="text-[#A89070]" />
                    <h3 className="serif text-xl mt-4">{w.title}</h3>
                    <p className="mt-3 text-[15px] text-[#544638] leading-relaxed">{w.text}</p>
                </div>
            ))}
        </div>
    </section>
);

const Ritual = ({ ritual, routine, slug }) => (
    <section className="mt-28 bg-[#262422] text-[#F2EDE2] rounded-sm p-8 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-12" data-testid="service-ritual">
        <div>
            <div className="eyebrow text-[#A89070]">Your Pre-Event Beauty Ritual</div>
            <h2 className="serif text-3xl md:text-4xl leading-[1.1] mt-4">{ritual.title}</h2>
            <p className="mt-6 text-[#F2EDE2]/80 leading-relaxed">{ritual.text}</p>
            <div className="mt-8 border-l-2 border-[#A89070] pl-5" data-testid="service-ritual-tip">
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#A89070]">Our tip</div>
                <p className="mt-2 text-[15px] text-[#F2EDE2]/85 leading-relaxed">{ritual.tip}</p>
            </div>
        </div>
        <div>
            <div className="eyebrow text-[#A89070]">Make It Part of Your Self-Care Routine</div>
            <h3 className="serif text-2xl mt-4">Your glow, your schedule.</h3>
            <ul className="mt-8 space-y-6" data-testid="service-routine">
                {routine.map((r) => (
                    <li key={r.title} className="border-t border-[#F2EDE2]/15 pt-5">
                        <div className="serif text-xl">{r.title}</div>
                        <p className="mt-2 text-[15px] text-[#F2EDE2]/75 leading-relaxed">{r.text}</p>
                    </li>
                ))}
            </ul>
            <Link to={`/book/${slug}`} data-testid="service-ritual-book-btn" className="inline-flex items-center gap-3 mt-10 bg-[#F2EDE2] text-[#262422] px-7 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-[#A89070] hover:text-[#F2EDE2] transition-colors duration-300">
                Reserve Your Glow <ArrowRight size={14} />
            </Link>
        </div>
    </section>
);

export const ServiceExtendedDetails = ({ details, slug }) => (
    <div data-testid="service-extended-details">
        {details.journey && <Journey steps={details.journey} />}
        {details.why && <Why intro={details.why_intro} items={details.why} />}
        {details.ritual && <Ritual ritual={details.ritual} routine={details.routine || []} slug={slug} />}
        {details.closing && (
            <p className="mt-20 serif italic text-2xl md:text-3xl leading-snug text-[#262422] max-w-3xl" data-testid="service-closing">
                {details.closing}
            </p>
        )}
        {details.disclaimer && (
            <p className="mt-10 text-xs text-[#8C7E6A] leading-relaxed max-w-3xl italic" data-testid="service-disclaimer">
                {details.disclaimer}
            </p>
        )}
    </div>
);
